// Ширина меню (0.22.4, жалоба владельца 31.10.2026: правая граница меню не двигается, полные
// названия ветвей «Структуры» не видны). Шире 900 px правую границу меню тянут мышью, как
// в Notion и Проводнике, или клавишами: ← → по 16 px, Home и End — пределы, двойной щелчок —
// ширина по умолчанию (PanelResizer, как граница колонки умной вставки).
//
// Отступление от MUST «Табеля» «Ширина меню — size-sidebar» (решение владельца 31.10.2026;
// строка в «Решениях после выкатки» plan.md 028): по умолчанию ширина та же — size-sidebar,
// 256 px; шире — только если пользователь потянул границу. Предел — min(520 px, 40 % окна).
// Свёрнутое меню, режим фокуса и меню поверх экрана до 900 px — без разделителя (AppShell).
// Ширина хранится на этом устройстве, как плотность таблиц; хранилище закрыто — ширина живёт
// до перезагрузки вкладки.
import { PanelResizer } from '@kp/ui';
import { useCallback, useState, useSyncExternalStore } from 'react';

/** size-sidebar «Табеля»: ширина по умолчанию и наименьшая. */
export const SIDEBAR_WIDTH = 256;
const SIDEBAR_MAX = 520;
/** Доля окна, шире которой меню не тянется: листу остаётся больше половины. */
const SIDEBAR_MAX_SHARE = 0.4;
const SIDEBAR_STEP = 16;
export const SIDEBAR_WIDTH_KEY = 'kp:sidebar-width';

/** Наибольшая ширина меню при ширине окна viewport. */
export function sidebarMaxWidth(viewport: number): number {
  return Math.max(SIDEBAR_WIDTH, Math.min(SIDEBAR_MAX, Math.floor(viewport * SIDEBAR_MAX_SHARE)));
}

function readWidth(): number | null {
  try {
    const stored = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  } catch {
    // Хранилище закрыто — ширина по умолчанию.
    return null;
  }
}

function saveWidth(width: number | null): void {
  try {
    if (width === null) window.localStorage.removeItem(SIDEBAR_WIDTH_KEY);
    else window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
  } catch {
    // Хранилище закрыто — ширина живёт до перезагрузки вкладки.
  }
}

function subscribeViewport(onChange: () => void): () => void {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
}

export interface SidebarWidth {
  /** Ширина меню сейчас — в пределах для этого окна. */
  width: number;
  max: number;
  /** Ширина на время перетаскивания: без записи. */
  resize: (width: number) => void;
  /** Ширина выбрана — запомнить; ширина по умолчанию стирает запись. */
  commit: (width: number) => void;
}

export function useSidebarWidth(): SidebarWidth {
  const [chosen, setChosen] = useState<number | null>(readWidth);
  const viewport = useSyncExternalStore(
    subscribeViewport,
    () => window.innerWidth,
    () => 0,
  );
  const max = sidebarMaxWidth(viewport);
  // Окно сузили — меню не шире предела; запись остаётся, в широком окне ширина вернётся.
  const width = Math.min(Math.max(chosen ?? SIDEBAR_WIDTH, SIDEBAR_WIDTH), max);
  const resize = useCallback((next: number) => setChosen(next), []);
  const commit = useCallback((next: number) => {
    const value = next === SIDEBAR_WIDTH ? null : next;
    setChosen(value);
    saveWidth(value);
  }, []);
  return { width, max, resize, commit };
}

export function SidebarResizer({ sidebar }: { sidebar: SidebarWidth }) {
  return (
    <PanelResizer
      className="kp-sidebar-resizer"
      label="Ширина меню"
      value={sidebar.width}
      min={SIDEBAR_WIDTH}
      max={sidebar.max}
      step={SIDEBAR_STEP}
      defaultValue={SIDEBAR_WIDTH}
      title="Потяните, чтобы изменить ширину меню. Двойной щелчок — ширина по умолчанию"
      onResize={sidebar.resize}
      onCommit={sidebar.commit}
    />
  );
}
